<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
-->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ isEditMode ? 'Edit Work Site' : 'Add Work Site' }}
      </oxd-text>
      <oxd-divider />

      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="workSite.name"
                label="Site Name"
                :rules="rules.name"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="workSite.address"
                label="Address"
                :rules="rules.address"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="workSite.latitude"
                label="Latitude"
                :rules="rules.latitude"
                type="number"
                step="any"
                required
                @change="onCoordsChanged"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="workSite.longitude"
                label="Longitude"
                :rules="rules.longitude"
                type="number"
                step="any"
                required
                @change="onCoordsChanged"
              />
            </oxd-grid-item>
            <oxd-grid-item v-if="workSite.fenceType === 'circle'">
              <oxd-input-field
                v-model="workSite.radiusMeters"
                label="Geofence Radius (metres)"
                :rules="rules.radiusMeters"
                type="number"
                required
                @change="onRadiusChanged"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-text tag="p" class="orangehrm-attendance-field-label">
                Active
              </oxd-text>
              <oxd-switch-input v-model="workSite.isActive" />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <div class="work-site-map-section">
            <div class="work-site-map-header">
              <oxd-text tag="p" class="orangehrm-attendance-field-label">
                Geofence Map
              </oxd-text>
              <div class="work-site-fence-type">
                <span class="work-site-fence-type-label">Fence type:</span>
                <button
                  type="button"
                  :class="['work-site-tile-btn', {active: workSite.fenceType === 'circle'}]"
                  @click="switchFenceType('circle')"
                >
                  Circle
                </button>
                <button
                  type="button"
                  :class="['work-site-tile-btn', {active: workSite.fenceType === 'polygon'}]"
                  @click="switchFenceType('polygon')"
                >
                  Polygon
                </button>
              </div>
              <span class="work-site-map-hint">
                <template v-if="workSite.fenceType === 'circle'">
                  Click on the map to place the work site marker. Drag the marker to reposition. The shaded circle shows the geofence boundary.
                </template>
                <template v-else>
                  Click the polygon tool
                  <strong>&#9650;</strong>
                  in the top-left toolbar to draw a custom geofence area. Click each vertex, then click the first point (or double-click) to close the shape.
                </template>
              </span>
            </div>
            <div ref="mapContainer" class="work-site-map"></div>
            <div class="work-site-map-tiles">
              <span class="work-site-map-tiles-label">Map style:</span>
              <button
                v-for="tile in tileOptions"
                :key="tile.id"
                type="button"
                :class="['work-site-tile-btn', {active: activeTile === tile.id}]"
                @click="switchTile(tile.id)"
              >
                {{ tile.label }}
              </button>
            </div>
          </div>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <oxd-button
            class="orangehrm-left-space"
            display-type="secondary"
            :label="$t('general.save')"
            type="submit"
          />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {ref} from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import useToast from '@/core/util/composable/useToast';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import {
  required,
  shouldNotExceedCharLength,
  digitsOnlyWithDecimalPointAndMinusSign,
  numericOnly,
} from '@/core/util/validation/rules';

// Fix Leaflet's broken default icon paths under webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const TILE_PROVIDERS = {
  osm: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
  },
  topo: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution:
      '&copy; <a href="https://opentopomap.org">OpenTopoMap</a> contributors',
    maxZoom: 17,
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution:
      'Tiles &copy; Esri &mdash; Source: Esri, Maxar, GeoEye, Earthstar Geographics',
    maxZoom: 18,
  },
};

const DEFAULT_LAT = 30.3753;  // Pakistan centre
const DEFAULT_LNG = 69.3451;  // Pakistan centre
const DEFAULT_ZOOM = 6;        // country-level zoom

const FENCE_STYLE = {
  color: '#FF6B00',
  fillColor: '#FF6B00',
  fillOpacity: 0.15,
  weight: 2,
};

export default {
  name: 'SaveWorkSite',
  props: {
    workSiteId: {
      type: Number,
      default: null,
    },
  },
  setup() {
    const {saveSuccess} = useToast();
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/work-sites',
    );
    const isLoading = ref(false);
    return {http, isLoading, saveSuccess};
  },
  data() {
    return {
      workSite: {
        name: '',
        address: '',
        latitude: '',
        longitude: '',
        radiusMeters: 200,
        isActive: true,
        fenceType: 'circle',
        polygonFence: null,
      },
      rules: {
        name: [required, shouldNotExceedCharLength(100)],
        address: [shouldNotExceedCharLength(255)],
        latitude: [
          required,
          digitsOnlyWithDecimalPointAndMinusSign,
          (v) =>
            (parseFloat(v) >= -90 && parseFloat(v) <= 90) ||
            'Must be between -90 and 90',
        ],
        longitude: [
          required,
          digitsOnlyWithDecimalPointAndMinusSign,
          (v) =>
            (parseFloat(v) >= -180 && parseFloat(v) <= 180) ||
            'Must be between -180 and 180',
        ],
        radiusMeters: [
          required,
          numericOnly,
          (v) =>
            (parseInt(v) >= 1 && parseInt(v) <= 50000) ||
            'Must be between 1 and 50000',
        ],
      },
      activeTile: 'osm',
      tileOptions: [
        {id: 'osm', label: 'Street'},
        {id: 'topo', label: 'Topo'},
        {id: 'satellite', label: 'Satellite'},
      ],
      // Non-reactive Leaflet internals
      _map: null,
      _marker: null,
      _circle: null,
      _tileLayer: null,
      _drawnItems: null,
      _drawControl: null,
      _polygonLayer: null,
      _mapClickHandler: null,
    };
  },
  computed: {
    isEditMode() {
      return !!this.workSiteId;
    },
  },
  async created() {
    // intentionally empty — data loading moved to mounted() so the map
    // is always initialised AFTER the work-site data is available.
  },
  async mounted() {
    if (this.isEditMode) {
      await this.loadWorkSite();
    }
    this.$nextTick(() => {
      this.initMap();
    });
  },
  beforeUnmount() {
    if (this._map) {
      this._map.remove();
      this._map = null;
    }
  },
  methods: {
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Map initialisation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    initMap() {
      const container = this.$refs.mapContainer;
      if (!container) return;

      const lat = parseFloat(this.workSite.latitude) || DEFAULT_LAT;
      const lng = parseFloat(this.workSite.longitude) || DEFAULT_LNG;
      const radius = parseInt(this.workSite.radiusMeters) || 200;

      this._map = L.map(container, {zoomControl: true}).setView(
        [lat, lng],
        DEFAULT_ZOOM,
      );

      const provider = TILE_PROVIDERS[this.activeTile];
      this._tileLayer = L.tileLayer(provider.url, {
        attribution: provider.attribution,
        maxZoom: provider.maxZoom,
      }).addTo(this._map);

      // Feature group for drawn polygons
      this._drawnItems = new L.FeatureGroup();
      this._map.addLayer(this._drawnItems);

      // Polygon draw events
      this._map.on(L.Draw.Event.CREATED, (e) => {
        this._drawnItems.clearLayers();
        this._polygonLayer = e.layer;
        this._drawnItems.addLayer(this._polygonLayer);
        const coords = this._polygonLayer
          .getLatLngs()[0]
          .map((p) => [p.lat, p.lng]);
        this.workSite.polygonFence = JSON.stringify(coords);
        const center = this._polygonLayer.getBounds().getCenter();
        this.workSite.latitude = parseFloat(center.lat.toFixed(6));
        this.workSite.longitude = parseFloat(center.lng.toFixed(6));
      });

      this._map.on(L.Draw.Event.EDITED, () => {
        const layers = this._drawnItems.getLayers();
        if (layers.length > 0) {
          const coords = layers[0]
            .getLatLngs()[0]
            .map((p) => [p.lat, p.lng]);
          this.workSite.polygonFence = JSON.stringify(coords);
          const center = layers[0].getBounds().getCenter();
          this.workSite.latitude = parseFloat(center.lat.toFixed(6));
          this.workSite.longitude = parseFloat(center.lng.toFixed(6));
        }
      });

      this._map.on(L.Draw.Event.DELETED, () => {
        this._polygonLayer = null;
        this.workSite.polygonFence = null;
      });

      if (this.workSite.fenceType === 'polygon') {
        this.activatePolygonMode(true);
      } else {
        this.activateCircleMode();
        if (this.workSite.latitude !== '' && this.workSite.longitude !== '') {
          this.placeMarkerAndCircle(lat, lng, radius);
        }
      }
    },

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Mode management â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    activateCircleMode() {
      if (this._drawControl) {
        this._map.removeControl(this._drawControl);
        this._drawControl = null;
      }
      this._mapClickHandler = (e) => {
        const {lat: clickLat, lng: clickLng} = e.latlng;
        this.workSite.latitude = parseFloat(clickLat.toFixed(6));
        this.workSite.longitude = parseFloat(clickLng.toFixed(6));
        this.placeMarkerAndCircle(
          this.workSite.latitude,
          this.workSite.longitude,
          parseInt(this.workSite.radiusMeters) || 200,
        );
      };
      this._map.on('click', this._mapClickHandler);
    },

    activatePolygonMode(restoreExisting) {
      if (this._mapClickHandler) {
        this._map.off('click', this._mapClickHandler);
        this._mapClickHandler = null;
      }
      if (this._circle) {
        this._map.removeLayer(this._circle);
        this._circle = null;
      }
      if (this._marker) {
        this._map.removeLayer(this._marker);
        this._marker = null;
      }

      this._drawControl = new L.Control.Draw({
        edit: {featureGroup: this._drawnItems},
        draw: {
          polygon: {
            allowIntersection: false,
            shapeOptions: FENCE_STYLE,
          },
          circle: false,
          rectangle: false,
          marker: false,
          polyline: false,
          circlemarker: false,
        },
      });
      this._map.addControl(this._drawControl);

      if (restoreExisting && this.workSite.polygonFence) {
        try {
          const coords = JSON.parse(this.workSite.polygonFence);
          this._polygonLayer = L.polygon(coords, FENCE_STYLE);
          this._drawnItems.addLayer(this._polygonLayer);
          this._map.fitBounds(this._polygonLayer.getBounds());
        } catch (_) {
          // ignore invalid stored polygon
        }
      }
    },

    switchFenceType(type) {
      if (!this._map || type === this.workSite.fenceType) return;
      this.workSite.fenceType = type;
      if (type === 'circle') {
        this._drawnItems.clearLayers();
        this._polygonLayer = null;
        this.workSite.polygonFence = null;
        this.activateCircleMode();
        const lat = parseFloat(this.workSite.latitude);
        const lng = parseFloat(this.workSite.longitude);
        const radius = parseInt(this.workSite.radiusMeters) || 200;
        if (!isNaN(lat) && !isNaN(lng)) {
          this.placeMarkerAndCircle(lat, lng, radius);
        }
      } else {
        this.activatePolygonMode(false);
      }
    },

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Circle helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    placeMarkerAndCircle(lat, lng, radiusMeters) {
      if (!this._map) return;

      if (this._marker) {
        this._marker.setLatLng([lat, lng]);
      } else {
        this._marker = L.marker([lat, lng], {draggable: true})
          .addTo(this._map)
          .bindPopup('Work site location')
          .openPopup();

        this._marker.on('dragend', (e) => {
          const pos = e.target.getLatLng();
          this.workSite.latitude = parseFloat(pos.lat.toFixed(6));
          this.workSite.longitude = parseFloat(pos.lng.toFixed(6));
          this.updateCircle(
            pos.lat,
            pos.lng,
            parseInt(this.workSite.radiusMeters) || 200,
          );
        });
      }

      if (this._circle) {
        this._circle.setLatLng([lat, lng]);
        this._circle.setRadius(radiusMeters);
      } else {
        this._circle = L.circle([lat, lng], {
          ...FENCE_STYLE,
          radius: radiusMeters,
        }).addTo(this._map);
      }

      this._map.setView([lat, lng], this._map.getZoom());
    },

    updateCircle(lat, lng, radiusMeters) {
      if (!this._circle || !this._map) return;
      this._circle.setLatLng([lat, lng]);
      this._circle.setRadius(radiusMeters);
    },

    switchTile(tileId) {
      if (!this._map || tileId === this.activeTile) return;
      this.activeTile = tileId;
      if (this._tileLayer) {
        this._map.removeLayer(this._tileLayer);
      }
      const provider = TILE_PROVIDERS[tileId];
      this._tileLayer = L.tileLayer(provider.url, {
        attribution: provider.attribution,
        maxZoom: provider.maxZoom,
      }).addTo(this._map);
    },

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Input sync â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    onCoordsChanged() {
      if (this.workSite.fenceType !== 'circle') return;
      const lat = parseFloat(this.workSite.latitude);
      const lng = parseFloat(this.workSite.longitude);
      const radius = parseInt(this.workSite.radiusMeters) || 200;
      if (!isNaN(lat) && !isNaN(lng)) {
        this.placeMarkerAndCircle(lat, lng, radius);
      }
    },

    onRadiusChanged() {
      if (this.workSite.fenceType !== 'circle') return;
      const lat = parseFloat(this.workSite.latitude);
      const lng = parseFloat(this.workSite.longitude);
      const radius = parseInt(this.workSite.radiusMeters) || 200;
      if (!isNaN(lat) && !isNaN(lng)) {
        this.updateCircle(lat, lng, radius);
      }
    },

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ CRUD â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    async loadWorkSite() {
      this.isLoading = true;
      try {
        const response = await this.http.get(this.workSiteId);
        const data = response.data?.data;
        if (data) {
          this.workSite = {
            name: data.name,
            address: data.address ?? '',
            latitude: data.latitude,
            longitude: data.longitude,
            radiusMeters: data.radiusMeters,
            isActive: data.isActive,
            fenceType: data.fenceType ?? 'circle',
            polygonFence: data.polygonFence ?? null,
          };
        }
      } finally {
        this.isLoading = false;
      }
    },

    async onSave() {
      this.isLoading = true;
      try {
        const isPolygon = this.workSite.fenceType === 'polygon';
        const payload = {
          name: this.workSite.name,
          address: this.workSite.address || null,
          latitude: parseFloat(this.workSite.latitude),
          longitude: parseFloat(this.workSite.longitude),
          radiusMeters: isPolygon ? 0 : parseInt(this.workSite.radiusMeters),
          isActive: this.workSite.isActive,
          fenceType: this.workSite.fenceType,
          polygonFence: isPolygon ? this.workSite.polygonFence : null,
        };

        if (this.isEditMode) {
          await this.http.update(this.workSiteId, payload);
        } else {
          await this.http.create(payload);
        }
        this.saveSuccess();
        navigate('/attendance/workSiteList');
      } finally {
        this.isLoading = false;
      }
    },

    onCancel() {
      navigate('/attendance/workSiteList');
    },
  },
};
</script>

<style scoped>
.work-site-map-section {
  width: 100%;
  padding: 4px 0;
}

.work-site-map-header {
  margin-bottom: 8px;
}

.work-site-map-hint {
  font-size: 0.8rem;
  color: #777;
  display: block;
  margin-top: 4px;
}

.work-site-fence-type {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 8px 0 4px;
}

.work-site-fence-type-label {
  font-size: 0.85rem;
  color: #555;
}

.work-site-map {
  width: 100%;
  height: 420px;
  border: 1px solid #d9dee3;
  border-radius: 4px;
  z-index: 0;
}

.work-site-map-tiles {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.work-site-map-tiles-label {
  font-size: 0.85rem;
  color: #555;
}

.work-site-tile-btn {
  padding: 4px 12px;
  font-size: 0.82rem;
  border: 1px solid #d9dee3;
  border-radius: 3px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
}

.work-site-tile-btn:hover {
  background: #f5f5f5;
}

.work-site-tile-btn.active {
  background: #ff7b1d;
  color: #fff;
  border-color: #ff7b1d;
}
</style>
